import projectsData from './projects.data.json';
import type {
  ChatMessage,
  ChatRepo,
  Conversation,
  LegalMilestone,
  Plan,
  Project,
  SavedReport,
  SupportAgent,
  WatchAlert,
  WatchlistItem,
} from './chatRepo';

/**
 * mockChatRepo — the in-memory ChatRepo backed by a copy of
 * `projects.data.json`. It mirrors the demo's seed data (opening turn, sample
 * threads, watchlist, support agent) so the UI renders real content with no
 * backend. Swapped for the api-client implementation in `main.tsx` only.
 */

interface ProjectsFile {
  projects: Record<string, Project>;
}

const PROJECTS = (projectsData as unknown as ProjectsFile).projects;

const SUPPORT_AGENT: SupportAgent = {
  name: 'Chuyên gia TrustLand',
  role: 'Luật sư & thẩm định viên',
  initial: 'T',
};

const WATCHLIST: WatchlistItem[] = [
  { id: 'p1', name: 'The Sun Avenue', loc: 'TP. Thủ Đức', score: 78, confidence: 'amber', hue: 158 },
  { id: 'p3', name: 'KDC Phúc Lộc Garden', loc: 'Long An', score: 41, confidence: 'red', hue: 35 },
  { id: 'cw1', name: 'The Peak Garden', loc: 'Quận 7', score: 38, confidence: 'red', hue: 35 },
];

/** Saved PDF reports (demo `REPORTS`, lines 1090–1098). */
const REPORTS: SavedReport[] = [
  {
    id: 'rp1',
    pid: 'p1',
    name: 'The Sun Avenue',
    type: 'Căn hộ',
    loc: 'An Phú, TP. Thủ Đức',
    score: 78,
    confidence: 'amber',
    date: '08/06/2026',
    size: '1,8 MB',
    pages: 12,
    sources: 14,
    hue: 158,
    status: 'ready',
  },
  {
    id: 'rp2',
    pid: 'p2',
    name: 'Vinhomes Central Park',
    type: 'Căn hộ',
    loc: 'Bình Thạnh, TP.HCM',
    score: 88,
    confidence: 'green',
    date: '05/06/2026',
    size: '2,1 MB',
    pages: 14,
    sources: 18,
    hue: 175,
    status: 'ready',
  },
  {
    id: 'rp3',
    pid: 'p3',
    name: 'KDC Phúc Lộc Garden',
    type: 'Đất nền',
    loc: 'Đức Hòa, Long An',
    score: 41,
    confidence: 'red',
    date: '02/06/2026',
    size: '1,2 MB',
    pages: 9,
    sources: 7,
    hue: 35,
    status: 'ready',
  },
];

/** Watchlist change alerts (demo `ALERTS`, lines 1100–1110). */
const ALERTS: WatchAlert[] = [
  {
    id: 'a1',
    pid: 'p1',
    sev: 'amber',
    icon: 'alert',
    title: 'The Sun Avenue · tiến độ bàn giao lùi 1 tháng',
    detail: 'Chủ đầu tư thông báo dời mốc bàn giao sang Q4/2026.',
    when: '2 giờ trước',
    src: 'Thông báo CĐT',
  },
  {
    id: 'a2',
    pid: 'p3',
    sev: 'red',
    icon: 'alert',
    title: 'Phúc Lộc Garden · cảnh báo quy hoạch mới',
    detail: 'Một phần diện tích được rà soát vào quy hoạch đất giao thông.',
    when: 'Hôm qua',
    src: 'Cổng QH Long An',
  },
  {
    id: 'a3',
    pid: 'p2',
    sev: 'green',
    icon: 'trend',
    title: 'Vinhomes Central Park · giá thứ cấp +1,2%',
    detail: 'Ghi nhận 6 giao dịch mới trong 30 ngày, thanh khoản tốt.',
    when: '3 ngày trước',
    src: 'TrustLand Price Index',
  },
];

/** Legal-calendar milestones (demo `MILESTONES`, lines 1110–1114). */
const MILESTONES: LegalMilestone[] = [
  { id: 'm1', pid: 'p1', label: 'Dự kiến cấp sổ hồng đợt 2', when: 'Q3 2026', s: 'amber' },
  { id: 'm2', pid: 'p2', label: 'Hết hạn bảo hành kỹ thuật toà Park', when: 'T08 2026', s: 'green' },
  { id: 'm3', pid: 'p3', label: 'Hạn nghiệm thu hạ tầng giai đoạn 1', when: 'Q4 2026', s: 'red' },
];

/** Subscription plans (demo `PLANS`, lines 1761–1766). */
const PLANS: Plan[] = [
  {
    name: 'Free',
    price: '0đ',
    per: '',
    note: 'Cho người mới biết nền tảng',
    feats: ['3–5 lượt tra cứu / tháng', 'Tầng dữ liệu 1–2', 'Preview infographic, đọc blog'],
  },
  {
    name: 'Plus',
    price: '300k',
    per: '/tháng',
    note: 'Người mua nghiêm túc, sale nhỏ',
    feats: ['50–100 lượt / tháng', 'Tải infographic · 5–10 AI video', 'Watchlist 10–20 dự án'],
  },
  {
    name: 'Pro',
    price: '500k',
    per: '/tháng',
    note: 'Sale chuyên nghiệp, luật sư, tư vấn',
    feats: ['Gần như không giới hạn lượt', 'Tầng dữ liệu 3–4 + expert note', 'PDF sâu · 20+ video · mini KPI'],
    hot: true,
  },
  {
    name: 'B2B SaaS',
    price: '5–20tr',
    per: '/tháng',
    note: 'Môi giới, luật sư, auditor',
    feats: ['Data pool dùng chung', 'Báo cáo white-label', 'Data API tích hợp'],
  },
];

/** The opening turn shown in every fresh conversation. */
const OPENING: ChatMessage[] = [
  {
    id: 'op-1',
    sender: 'bot',
    type: 'text',
    time: '09:24',
    text: 'Xin chào! Mình là <b>Trust Agent</b> — trợ lý kiểm chứng bất động sản của TrustLand. Mình giúp bạn <b>soi pháp lý, quy hoạch, giá và rủi ro</b> của một dự án <i>trước khi bạn đặt cọc</i>.',
  },
  {
    id: 'op-2',
    sender: 'bot',
    type: 'text',
    time: '09:24',
    text: 'Mọi câu trả lời đều kèm <b>nguồn trích dẫn</b> và <b>mức độ tin cậy</b>. Bạn muốn bắt đầu với điều gì?',
  },
  {
    id: 'op-3',
    type: 'chips',
    chips: [
      { label: 'Tư vấn cá nhân hoá theo nhu cầu', action: 'wizard', icon: 'spark' },
      { label: 'Kiểm tra pháp lý một dự án', action: 'check', icon: 'shield' },
      { label: 'So sánh 2 dự án', action: 'compare', icon: 'scale' },
      { label: 'Tính dòng tiền cho thuê', action: 'cashflow', icon: 'chart' },
      { label: 'Dự án mới từ batdongsan', action: 'crawled', icon: 'search' },
    ],
  },
];

const CONVERSATIONS: Conversation[] = [
  { id: 'c1', pid: null, title: 'Cuộc trò chuyện mới', messages: OPENING },
  {
    id: 'c2',
    pid: 'p1',
    title: 'Pháp lý The Sun Avenue',
    messages: [
      { id: 'c2-1', sender: 'user', type: 'text', time: '09:10', text: 'The Sun Avenue pháp lý ổn không?' },
      {
        id: 'c2-2',
        sender: 'bot',
        type: 'text',
        time: '09:10',
        text: 'Dự án đã có <b>sổ hồng riêng từng căn</b> và đúng quy hoạch — mức tin cậy Trung bình. Mở Hồ sơ thẩm định để xem 6 nhóm dữ liệu.',
      },
    ],
  },
  {
    id: 'c3',
    pid: 'p3',
    title: 'Cảnh báo Phúc Lộc Garden',
    messages: [
      { id: 'c3-1', sender: 'user', type: 'text', time: 'Hôm qua', text: 'Đất nền Phúc Lộc Garden có nên mua?' },
      {
        id: 'c3-2',
        sender: 'bot',
        type: 'text',
        time: 'Hôm qua',
        text: '<b>Cảnh báo:</b> chưa có sổ từng nền, một phần dính quy hoạch đất giao thông — mức tin cậy Thấp.',
      },
    ],
  },
  {
    id: 'c4',
    pid: null,
    title: 'Vốn 3 tỷ nên mua gì?',
    messages: [
      { id: 'c4-1', sender: 'user', type: 'text', time: '2 ngày', text: 'Vốn 3 tỷ mua để ở nên chọn dự án nào?' },
      {
        id: 'c4-2',
        sender: 'bot',
        type: 'text',
        time: '2 ngày',
        text: 'Ưu tiên dự án pháp lý rõ, đúng tầm giá. Mình gợi ý vài lựa chọn đã kiểm chứng.',
      },
    ],
  },
];

export const mockChatRepo: ChatRepo = {
  listProjects() {
    return Promise.resolve(Object.values(PROJECTS));
  },
  getProject(id) {
    return Promise.resolve(PROJECTS[id]);
  },
  listConversations() {
    return Promise.resolve(CONVERSATIONS);
  },
  listMessages(threadId) {
    const conv = CONVERSATIONS.find((c) => c.id === threadId);
    return Promise.resolve(conv ? conv.messages : []);
  },
  listWatchlist() {
    // Return a copy so React Query sees a new reference after a mutation.
    return Promise.resolve([...WATCHLIST]);
  },
  addToWatchlist(projectId) {
    if (WATCHLIST.some((w) => w.id === projectId)) return Promise.resolve(false);
    const project = PROJECTS[projectId];
    if (!project) return Promise.resolve(false);
    WATCHLIST.push({
      id: project.id,
      name: project.name,
      loc: project.loc,
      score: project.score,
      confidence: project.confidence,
      hue: project.hue,
    });
    return Promise.resolve(true);
  },
  removeFromWatchlist(projectId) {
    const i = WATCHLIST.findIndex((w) => w.id === projectId);
    if (i === -1) return Promise.resolve(false);
    WATCHLIST.splice(i, 1);
    return Promise.resolve(true);
  },
  listReports() {
    return Promise.resolve(REPORTS);
  },
  listAlerts() {
    return Promise.resolve(ALERTS);
  },
  listMilestones() {
    return Promise.resolve(MILESTONES);
  },
  listPlans() {
    return Promise.resolve(PLANS);
  },
  getSupportAgent() {
    return Promise.resolve(SUPPORT_AGENT);
  },
};
